import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Activity, LogIn, UserPlus, Pencil, Glasses, Send, Download, Trash2, ShieldCheck, FileUp } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ACTION_META = {
  ACCESSO: { label: "Accesso", icon: LogIn, tone: "bg-slate-100 text-slate-600" },
  CREA_CLIENTE: { label: "Creazione cliente", icon: UserPlus, tone: "bg-emerald-100 text-emerald-700" },
  MODIFICA_CLIENTE: { label: "Modifica cliente", icon: Pencil, tone: "bg-sky-100 text-sky-700" },
  MODIFICA_DATI_OTTICI: { label: "Modifica dati ottici", icon: Glasses, tone: "bg-indigo-100 text-indigo-700" },
  INVIO_CAMPAGNA: { label: "Invio campagna", icon: Send, tone: "bg-amber-100 text-amber-700" },
  ESPORTAZIONE: { label: "Esportazione dati", icon: Download, tone: "bg-red-100 text-red-700" },
  CREA_UTENTE: { label: "Creazione utente", icon: UserPlus, tone: "bg-emerald-100 text-emerald-700" },
  ELIMINA_UTENTE: { label: "Eliminazione utente", icon: Trash2, tone: "bg-red-100 text-red-700" },
  ELIMINA_CLIENTE: { label: "Eliminazione cliente", icon: Trash2, tone: "bg-red-100 text-red-700" },
  CARICA_DOCUMENTO: { label: "Caricamento documento", icon: FileUp, tone: "bg-indigo-100 text-indigo-700" },
  ELIMINA_DOCUMENTO: { label: "Eliminazione documento", icon: Trash2, tone: "bg-red-100 text-red-700" },
  IMPORTAZIONE: { label: "Importazione dati", icon: FileUp, tone: "bg-violet-100 text-violet-700" },
};

export default function LogAttivita() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const { data } = await api.get("/audit", { params: filter !== "all" ? { azione: filter } : {} });
    setLogs(data);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const fmt = (ts) => { try { return new Date(ts).toLocaleString("it-IT"); } catch { return ts; } };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-slate-900">Log Attività</h1>
          <p className="text-sm text-slate-500 mt-1">Audit trail completo · accessi, modifiche ed esportazioni.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56 bg-white" data-testid="log-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le operazioni</SelectItem>
            {Object.entries(ACTION_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Operazione</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Utente</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Dettaglio</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">IP</th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Data / Ora</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {logs.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500">Nessuna attività registrata.</td></tr>}
            {logs.map((l) => {
              const m = ACTION_META[l.azione] || { label: l.azione, icon: Activity, tone: "bg-slate-100 text-slate-600" };
              const Icon = m.icon;
              return (
                <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-lg text-xs font-medium ${m.tone}`}>
                      <Icon className="h-3.5 w-3.5" /> {m.label}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="font-medium text-slate-900">{l.utente}</div>
                    <Badge variant="outline" className="text-[10px] mt-0.5">{l.ruolo}</Badge>
                  </td>
                  <td className="px-5 py-3.5 hidden lg:table-cell text-slate-600 max-w-xs truncate">{l.dettaglio}</td>
                  <td className="px-5 py-3.5 hidden md:table-cell font-mono text-xs text-slate-500">{l.ip || "—"}</td>
                  <td className="px-5 py-3.5 text-slate-600 whitespace-nowrap">{fmt(l.timestamp)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="flex items-center gap-2 text-xs text-slate-400"><ShieldCheck className="h-4 w-4" /> Il registro è visibile solo agli amministratori e conserva utente, data e operazione.</p>
    </div>
  );
}
